import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
	Autocomplete,
	TextField,
	CircularProgress,
	Box,
	Alert,
	Button,
	FormHelperText,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Typography,
	Divider,
	Modal,
	FormGroup,
	IconButton,
	Chip,
	Popper,
	Tooltip,
} from "@mui/material";
import "./ManageSiteMachines.css";
import Datagrid from "../../components/Datagrid/Datagrid";
import { useSite } from "../../context/siteContext";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { AddCircle, ChevronLeft, ChevronRight, Delete } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { ALERT_MESSAGES, BUTTON, LABELS, MENU_ITEMS, TABLE_HEADERS, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getProvisionedMachines, provisionMachinesForCustomerSite, deleteProvisionsForCustomerSite, updateProvisionForCustomerSite, triggerDocumentSyncForMachine, triggerReload3DModelForMachine, triggerBulkReload3DModelForMachines, triggerStepSyncForSite, } from "../../services/manageSiteMachinesService";
import { askRefreshMode } from "../../utilities/refreshMode";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getMachines } from "../../services/manageMachinesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import DataGridSkeletonForEquipment from "../PageSkeletons/DataGridSkeletonForEquipment";

// Customer-facing 3D model states, derived server-side in getMachinesWithModels
// from the published model plus the latest sync request. Anything unrecognised
// falls back to "Not requested" rather than showing a raw internal status.
const MODEL_STATE_CHIP = {
    available: {
        label: "Available",
        color: "success",
        tooltip: "The 3D model is ready to view.",
    },
    available_updating: {
        label: "Updating",
        color: "info",
        tooltip: "A new 3D version is being generated. The current model is still available to view.",
    },
    in_progress: {
        label: "In progress",
        color: "info",
        tooltip: "The 3D model is being prepared. This may take up to 2 hours.",
    },
    not_requested: {
        label: "Not requested",
        color: "default",
        tooltip: "No 3D model has been requested for this machine yet.",
    },
    unavailable: {
        label: "Unavailable",
        color: "warning",
        tooltip: "No STEP file was found for this machine. Please contact support.",
    },
    failed: {
        label: "Failed",
        color: "error",
        tooltip: "The 3D model could not be generated. Please try again, or contact support.",
    },
};

/**
 * Values offered by the "3D Model Sync" column filter: every label the chip map can
 * render, in its order, so the filter list and the chips can never disagree.
 */
const MODEL_STATE_FILTER_OPTIONS = Object.values(MODEL_STATE_CHIP).map((chip) => chip.label);

/**
 * Plain cell value for the "3D Model Sync" column, used by the column filter and the
 * toolbar search. The cell itself renders a chip plus buttons, so without this the
 * grid has nothing to filter on.
 *
 * Returns the chip LABEL rather than the state key so that the filter dropdown, the
 * search box and the chip all speak the same words; an unknown or missing state
 * resolves to "Not requested", exactly as the chip does. Accepts both the
 * x-data-grid v7 signature (value, row) and the Datagrid wrapper's older
 * single-params call used by its search.
 *
 * @param {unknown} value Cell value (v7) or the legacy params object.
 * @param {object} [row] The grid row (v7).
 * @returns {string} The chip label for the row's model_state.
 */
const modelStateLabelForRow = (value, row) => {
    const source = row || (value && typeof value === "object" && value.row) || {};
    const chip = MODEL_STATE_CHIP[String(source.model_state || "")] || MODEL_STATE_CHIP.not_requested;
    return chip.label;
};

const INITIAL_PROVISION_FORM = {
	machine_name: "",
	machine_number: "",
	global_product_code: "",
	machine_serial_number: "",
	provision_date: "",
	status: "active",
	site_name: "",
	cus_name: "",
	machine_id: "",
	site_id: "",
	cus_id: "",
	site_number: "",
	cus_number: "",
};

// Must not exceed the server's BULK_RELOAD_MAX_MACHINES cap on /reload-3d-model/bulk.
const BULK_STEP_SYNC_CHUNK = 500;


const normalizeSerial = (value = "") => value?.toString().trim().toLowerCase();
const LARGE_AUTOCOMPLETE_RESULT_LIMIT = 200;
const normalizeAutocompleteSearch = (value = "") =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const ManageSiteMachines = ({
	hideFilters = false,
	hideAdminFilters = false,
	showDocumentFetchColumn = false,
	selectedCustomer: externalSelectedCustomer = null,
	selectedSiteData: externalSelectedSiteData = null,
}) => {
	const { t } = useTranslation();
	const { selectedSite } = useSite();
	const { user: authUser, hasPermission } = useAuth();
	const isSuperAdmin = Number(authUser?.user_id) === 1;
	const toBool = (value, fallback = false) => {
		if (typeof value === "boolean") return value;
		if (typeof value === "number") return value === 1;
		if (typeof value === "string") {
			const normalized = value.trim().toLowerCase();
			if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
			if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
		}
		return fallback;
	};
	const [customers, setCustomers] = useState([]);
	const [sites, setSites] = useState([]);
	const [allSites, setAllSites] = useState([]);
	const [error, setError] = useState("");
	const [formErrors, setFormErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [sitesLoading, setSitesLoading] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [selectedSiteData, setSelectedSiteData] = useState(null);
	const [assignedMachines, setAssignedMachines] = useState([]);
	const [allProvisionedMachines, setAllProvisionedMachines] = useState([]);
	const [nonAssignedMachines, setNonAssignedMachines] = useState([]);
	const [newProvision, setNewProvision] = useState(false);
	const [machineSelected, setMachineSelected] = useState(null);
	const [submittingProvision, setSubmittingProvision] = useState(false);
	const [emptySites, setEmptySites] = useState(false);
	const [provisionCustomer, setProvisionCustomer] = useState(null);
	const [provisionSite, setProvisionSite] = useState(null);
	const [provisionSites, setProvisionSites] = useState([]);
	const [provisionFormData, setProvisionFormData] = useState(INITIAL_PROVISION_FORM);
	const [validationErrors, setValidationErrors] = useState({});
	const [errorMessage, setErrorMessage] = useState("");
	const [provisionServerError, setProvisionServerError] = useState("");
	const [selectedRows, setSelectedRows] = useState([]);
	const [editProvision, setEditProvision] = useState(false);
	const [editSubmitting, setEditSubmitting] = useState(false);
	const [editFormData, setEditFormData] = useState({
		id: "",
		machine_name: "",
		machine_number: "",
		machine_serial_number: "",
		status: t("MENU_ITEMS.ACTIVE"),
	});
	const [editValidationErrors, setEditValidationErrors] = useState({});
	const [editError, setEditError] = useState("");
	const [syncingMachineNumber, setSyncingMachineNumber] = useState("");
	const [reloadingMachineNumber, setReloadingMachineNumber] = useState("");
	const [siteStepSyncLoadingId, setSiteStepSyncLoadingId] = useState(null);
	const [bulkStepSyncing, setBulkStepSyncing] = useState(false);
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	const customerSiteSearchCacheRef = useRef(new WeakMap());
	const modalAutocompletePopper = useCallback(
		(props) => (
			<Popper
				{...props}
				placement="bottom-start"
				strategy="fixed"
				style={{ zIndex: 2000, ...props.style }}
			/>
		),
		[]
	);
	const modalAutocompleteProps = useMemo(
		() => ({
			disablePortal: false,
			PopperComponent: modalAutocompletePopper,
			PopperProps: {
				className: "modal-menu-popover",
				container: typeof document !== "undefined" ? document.body : undefined,
				modifiers: [
					{ name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
					{ name: "flip", enabled: true },
				],
			},
			slotProps: {
				popper: { className: "modal-menu-popover", sx: { zIndex: 2000 } },
				paper: { sx: { maxHeight: 300, overflowY: "auto" } },
				listbox: { sx: { maxHeight: 300, overflowY: "auto" } },
			},
			ListboxProps: { style: { maxHeight: 300, overflowY: "auto" } },
		}),
		[modalAutocompletePopper]
	);

	const actorRoleType = useMemo(
		() => String(authUser?.role?.role_type || authUser?.role?.role_name || "").trim().toLowerCase(),
		[authUser?.role?.role_type, authUser?.role?.role_name]
	);
	const actorSiteIds = useMemo(() => {
		const fromAllocs = Array.isArray(authUser?.allocations)
			? authUser.allocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0)
			: [];
		return Array.from(new Set(fromAllocs));
	}, [authUser?.allocations]);
	const effectiveActorSiteIds = useMemo(
		() => (actorSiteIds.length > 0 ? actorSiteIds : fallbackActorSiteIds),
		[actorSiteIds, fallbackActorSiteIds]
	);
	const actorHasAllSiteVisibility = useMemo(
		() =>
			Number(authUser?.user_id) === 1 ||
			((actorRoleType === "admin" || toBool(authUser?.role?.permissions?.machine_provision_management?.manage_all_sites, false)) && effectiveActorSiteIds.length === 0),
		[authUser?.user_id, actorRoleType, authUser?.role?.permissions?.machine_provision_management?.manage_all_sites, effectiveActorSiteIds.length]
	);
	const customerSiteFilterOptions = useCallback((options = [], { inputValue } = {}) => {
		const normalizedInput = normalizeAutocompleteSearch(inputValue);
		if (!normalizedInput) return options.slice(0, LARGE_AUTOCOMPLETE_RESULT_LIMIT);
		const filtered = [];
		for (const option of options) {
			let searchable = customerSiteSearchCacheRef.current.get(option);
			if (searchable === undefined) {
				searchable = normalizeAutocompleteSearch(
					`${option?.customer_name || ""} ${option?.customer_number || ""} ${option?.customer_site_name || ""} ${option?.customer_site_number || ""}`
				);
				customerSiteSearchCacheRef.current.set(option, searchable);
			}
			if (searchable.includes(normalizedInput)) filtered.push(option);
			if (filtered.length >= LARGE_AUTOCOMPLETE_RESULT_LIMIT) break;
		}
		return filtered;
	}, []);

	const handleTriggerDocumentFetch = async (row) => {
		const machineNumber = `${row?.machine_number || ""}`.trim();
		if (!machineNumber) {
			Swal.fire({
				icon: "warning",
				title: "Missing machine number",
				text: "Machine number is required to fetch documents.",
			});
			return;
		}

		setSyncingMachineNumber(machineNumber);
		try {
			const response = await triggerDocumentSyncForMachine(machineNumber, 20, "both");
			if (!response?.status) {
				throw new Error(response?.message || "Failed to queue document refresh request");
			}

			Swal.fire({
				icon: "success",
				title: "Refresh requested",
				text:
					response?.message ||
					`Document refresh request saved for machine ${machineNumber}. The Windows scheduler will process it on its next cycle.`,
				timer: 2200,
				showConfirmButton: false,
			});
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: "Request failed",
				text: error?.message || "Failed to queue document refresh request.",
			});
		} finally {
			setSyncingMachineNumber("");
		}
	};

	/**
	 * Queue a 3D/STEP reload for one machine row.
	 *
	 * @param {{machine_number: string, site_id: number, model_state?: string}} row Grid row.
	 * @returns {Promise<void>}
	 *
	 * Backs both the "Sync Machine 3D Model" icon in the 3D Model Sync column and the
	 * super-admin "Reload 3D Model" icon in the Actions column — they are two entry
	 * points to the same action, so the hard/simple prompt only has to be wired here.
	 * `model_state` changes the prompt's title, not whether it appears.
	 */
	const handleTriggerReload3DModel = async (row) => {
		const machineNumber = `${row?.machine_number || ""}`.trim();
		const siteId = Number(row?.site_id);
		if (!machineNumber || !Number.isFinite(siteId) || siteId <= 0) {
			Swal.fire({
				icon: "warning",
				title: "Missing machine data",
				text: "Site and machine number are required to reload the 3D model.",
			});
			return;
		}

		// Asked on every click, including for a machine with no published model yet.
		// With nothing published the two modes behave identically (no previous sync to
		// compare against, so the server converts either way), but prompting only
		// sometimes made the button feel broken — you cannot tell by looking at a row
		// whether this click will ask you something.
		const hasModel =
			String(row?.model_state || "") === "available" ||
			String(row?.model_state || "") === "available_updating";
		const mode = await askRefreshMode({
			title: hasModel ? "This machine already has a 3D model" : "Sync this machine's 3D model?",
			text:
				"Simple refresh updates the model only if a newer CAD file is found. " +
				"Hard refresh rebuilds it either way — use this if the model is missing or wrong.",
		});
		if (!mode) return;

		setReloadingMachineNumber(machineNumber);
		try {
			const response = await triggerReload3DModelForMachine(siteId, machineNumber, mode);
			if (!response?.status) {
				throw new Error(response?.message || "Failed to queue 3D model reload request");
			}

			// The server returns `request_id` when this machine already has an active
			// request, and `id` when it created a new one — so this tells them apart.
			const alreadyQueued = Boolean(response?.data?.request_id);

			Swal.fire(
				alreadyQueued
					? {
						icon: "info",
						title: "Already in progress",
						text: "This machine is already being processed. It may take up to 2 hours, depending on the queue. No action needed — you'll be notified when it's ready.",
					}
					: {
						icon: "success",
						title: "3D sync started",
						text: "This process may take up to 2 hours to complete. You'll be notified once it's done.",
					}
			);
			refreshProvisionedMachines();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: "3D sync failed",
				text: "Please try again or contact support if the issue persists.",
			});
		} finally {
			setReloadingMachineNumber("");
		}
	};

	/**
	 * Queue a STEP/3D sync for every machine at one bakery location.
	 *
	 * @param {{site_id: number, customer_site_name?: string}} row Grid row for the site.
	 * @returns {Promise<void>}
	 */
	const handleTriggerSiteStepSync = async (row) => {
		const siteId = Number(row?.site_id);
		const siteName = row?.customer_site_name || "this bakery location";
		if (!Number.isFinite(siteId) || siteId <= 0) {
			Swal.fire({
				icon: "warning",
				title: "Missing site data",
				text: "A bakery location is required to sync its 3D models.",
			});
			return;
		}

		const mode = await askRefreshMode({
			title: "Sync bakery location 3D models?",
			text:
				`Queue a 3D model sync for all machines at ${siteName}. ` +
				"Simple refresh updates only the models with a newer CAD file. " +
				"Hard refresh rebuilds every model at this location, which is far slower.",
		});
		if (!mode) return;

		setSiteStepSyncLoadingId(siteId);
		try {
			const response = await triggerStepSyncForSite(siteId, mode);
			if (!response?.status) {
				throw new Error(response?.message || "Failed to queue bakery location 3D model sync.");
			}
			Swal.fire({
				icon: "success",
				title: "3D sync started for all machines",
				text: "This may take up to 24 hours, depending on the number of machines and their complexity. You'll be notified once all machines are synced.",
			});
			refreshProvisionedMachines();
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: "3D sync failed",
				text: "Please try again or contact support if the issue persists.",
			});
		} finally {
			setSiteStepSyncLoadingId(null);
		}
	};

	/**
	 * Queues a STEP/3D model refresh for many machines in one click (super admin only).
	 *
	 * Operates on the checkbox selection when there is one, otherwise on every row
	 * currently listed (i.e. after the customer/bakery-location filters), so "refresh
	 * everything" needs no separate endpoint. Uses the per-machine endpoint rather than
	 * the per-site one because a selection can be an arbitrary subset of a site's
	 * machines — the site endpoint would queue machines the admin did not pick.
	 *
	 * The whole selection goes out as ONE request per 500 machines via
	 * /machines/reload-3d-model/bulk — it used to fire the single-machine endpoint once
	 * per row, which put 40+ POSTs on the wire inside the same second. Re-clicking is
	 * safe: machines that already have an active request come back in
	 * `skipped_already_active` instead of being queued twice.
	 *
	 * @returns {Promise<void>} Resolves once every chunk has been queued and the grid's
	 *   model-state chips have been re-pulled.
	 */
	const handleBulkStepSync = async () => {
		// MUI selection ids can arrive as strings while sm_machine_id is numeric.
		const selectedIdSet = new Set((selectedRows || []).map((id) => String(id)));
		const scope = selectedIdSet.size > 0
			? (assignedMachines || []).filter((row) => selectedIdSet.has(String(row?.sm_machine_id)))
			: (assignedMachines || []);
		const targets = scope.filter(
			(row) => `${row?.machine_number || ""}`.trim() && Number(row?.site_id) > 0
		);

		if (targets.length === 0) {
			Swal.fire({
				icon: "warning",
				title: "Nothing to sync",
				text: "No machines with a bakery location and machine number are available to refresh.",
			});
			return;
		}

		// One answer for the whole batch — the chunk loop below reuses it rather than
		// asking per machine, which for 500 rows would be 500 prompts.
		const mode = await askRefreshMode({
			title: "Refresh STEP files?",
			text:
				`Queue a 3D model sync for ${targets.length} machine(s). This may take up to 24 hours. ` +
				"Simple refresh updates only the models with a newer CAD file. " +
				"Hard refresh rebuilds all of them.",
		});
		if (!mode) return;

		setBulkStepSyncing(true);
		let queued = 0;
		let skipped = 0;
		let failed = 0;
		try {
			// One request per chunk, not per machine — the server caps a batch at 500.
			for (let i = 0; i < targets.length; i += BULK_STEP_SYNC_CHUNK) {
				const chunk = targets.slice(i, i + BULK_STEP_SYNC_CHUNK).map((row) => ({
					site_id: Number(row.site_id),
					machine_number: `${row.machine_number}`.trim(),
				}));
				// eslint-disable-next-line no-await-in-loop
				const response = await triggerBulkReload3DModelForMachines(chunk, mode);
				if (response?.status) {
					queued += Number(response?.data?.queued || 0);
					skipped += Number(response?.data?.skipped_already_active || 0);
				} else {
					failed += chunk.length;
				}
			}

			Swal.fire({
				icon: failed === 0 ? "success" : "warning",
				title: failed === 0 ? "3D sync started" : "3D sync partially started",
				html:
					`<b>${queued}</b> machine(s) queued for 3D model sync.` +
					(skipped > 0 ? `<br><b>${skipped}</b> already in progress — left as they are.` : "") +
					(failed > 0 ? `<br><b>${failed}</b> machine(s) could not be queued.` : "") +
					"<br>This may take up to 24 hours, depending on the number of machines and their complexity.",
			});
			refreshProvisionedMachines();
		} finally {
			setBulkStepSyncing(false);
		}
	};

	const normalizeProvisionRow = (row = {}) => ({
		machine_id: row.machine_id,
		machine_name: row.machine_name,
		machine_number: row.machine_number,
		global_product_code: row.global_product_code || "",
		sm_machine_product_name: row.machine_product_name || row.sm_machine_product_name,
		sm_machine_status: row.machine_status || row.sm_machine_status,
		sm_machine_id: row.id || row.sm_machine_id,
		customer_number: row.customer_number || row.cus_number,
		customer_name: row.customer_name || row.cus_name,
		customer_site_number: row.customer_site_number || row.site_number,
		customer_site_name: row.customer_site_name || row.site_name,
		site_id: row.site_id,
		cus_id: row.cus_id,
		model_state: row.model_state,
	});

	// Re-pull the provisioned machines so the 3D model status chip reflects the
	// request that was just queued, instead of waiting for a page reload.
	const refreshProvisionedMachines = async () => {
		try {
			const response = await getProvisionedMachines({ includeInactive: true });
			if (response?.status) {
				const normalized = response.data.map(normalizeProvisionRow);
				setAllProvisionedMachines(normalized);
				setAssignedMachines(normalized);
			}
		} catch (err) {
			// Non-fatal — the sync is queued either way, only the chip is stale.
			console.error("Failed to refresh 3D model status:", err);
		}
	};

	const applyProvisionFilters = (provisions, customer, site) => {
		if (site?.site_id) {
			return provisions.filter((row) => row.site_id === site.site_id);
		}
		if (customer?.cus_id) {
			return provisions.filter((row) => row.cus_id === customer.cus_id);
		}
		return provisions;
	};

	const resetProvisionForm = () => {
		setProvisionFormData(INITIAL_PROVISION_FORM);
		setValidationErrors({});
		setErrorMessage("");
		setProvisionServerError("");
		setMachineSelected(null);
		setProvisionCustomer(null);
		setProvisionSite(null);
		setProvisionSites([]);
	};

	// Handle change function for all fields
	const handleProvisionFormChange = (event) => {
		const { name, value } = event.target;
		setProvisionFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
		setValidationErrors((prev) => ({ ...prev, [name]: "" }));
		if (errorMessage) setErrorMessage("");
	};

	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (Number(authUser?.user_id) === 1 || actorSiteIds.length > 0 || !authUser?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const resp = await getUserAllocations(authUser.user_id);
				const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
				const ids = Array.from(
					new Set(
						allocations
							.map((a) => Number(a?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(ids);
			} catch (_err) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => { mounted = false; };
	}, [actorSiteIds, authUser?.user_id]);

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const [responseCustomers, responseSites, responseProvisioned] = await Promise.all([
					getCustomersCusSites(),
					getAllCustomerSites(),
					getProvisionedMachines({ includeInactive: true }),
				]);

				
				if (responseCustomers.status) {
					const filteredCustomers = responseCustomers.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					setCustomers(filteredCustomers);
				} else {
					setError(t('GENERAL.FAILED_TO_FETCH_CUSTOMERS'));
				}

				if (responseSites.status) {
					setAllSites(responseSites.data);
					setSites(responseSites.data);
				}

				if (responseProvisioned.status) {
					const normalized = responseProvisioned.data.map(normalizeProvisionRow);
					setAllProvisionedMachines(normalized);
					setAssignedMachines(normalized);
				}
			} catch (err) {
				setError(t('GENERAL.FAILED_TO_FETCH_CUSTOMERS_1') + " " + err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchInitialData();
	}, [selectedSite]);

	const scopedSites = useMemo(() => {
		if (actorHasAllSiteVisibility) return allSites || [];
		const allowedSet = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
		return (allSites || []).filter((site) => allowedSet.has(Number(site?.site_id)));
	}, [actorHasAllSiteVisibility, allSites, effectiveActorSiteIds]);
	const scopedSiteIdSet = useMemo(
		() => new Set((scopedSites || []).map((site) => Number(site?.site_id)).filter((id) => Number.isFinite(id) && id > 0)),
		[scopedSites]
	);
	const scopedAllProvisionedMachines = useMemo(() => {
		if (actorHasAllSiteVisibility) return allProvisionedMachines || [];
		return (allProvisionedMachines || []).filter((row) => scopedSiteIdSet.has(Number(row?.site_id)));
	}, [actorHasAllSiteVisibility, allProvisionedMachines, scopedSiteIdSet]);
	const scopedCustomers = useMemo(() => {
		if (actorHasAllSiteVisibility) return customers || [];
		const allowedCustomerIds = new Set(
			(scopedSites || [])
				.map((site) => Number(site?.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		return (customers || []).filter((customer) => allowedCustomerIds.has(Number(customer?.cus_id)));
	}, [actorHasAllSiteVisibility, customers, scopedSites]);

	useEffect(() => {
		if (!hideFilters) return;
		const nextCustomer = externalSelectedCustomer || null;
		const nextSite = externalSelectedSiteData || null;
		const currentCustomerId = Number(selectedCustomer?.cus_id || 0);
		const nextCustomerId = Number(nextCustomer?.cus_id || 0);
		const currentSiteId = Number(selectedSiteData?.site_id || 0);
		const nextSiteId = Number(nextSite?.site_id || 0);
		if (currentCustomerId === nextCustomerId && currentSiteId === nextSiteId) return;

		setSelectedCustomer(nextCustomer);
		setSelectedSiteData(nextSite);
		setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, nextCustomer, nextSite));
		setNonAssignedMachines([]);
		if (nextCustomer) {
			const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(nextCustomer?.cus_id));
			setSites(filtered);
			setEmptySites(filtered.length === 0);
			return;
		}
		setSites(scopedSites);
		setEmptySites(false);
	}, [
		hideFilters,
		externalSelectedCustomer,
		externalSelectedSiteData,
		selectedCustomer?.cus_id,
		selectedSiteData?.site_id,
		scopedAllProvisionedMachines,
		scopedSites,
	]);

	useEffect(() => {
		if (selectedCustomer && !scopedCustomers.some((customer) => Number(customer?.cus_id) === Number(selectedCustomer?.cus_id))) {
			setSelectedCustomer(null);
			setSelectedSiteData(null);
			setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, null, null));
		}
	}, [selectedCustomer, scopedCustomers, scopedAllProvisionedMachines]);

	useEffect(() => {
		if (selectedSiteData && !scopedSites.some((site) => Number(site?.site_id) === Number(selectedSiteData?.site_id))) {
			setSelectedSiteData(null);
		}
	}, [selectedSiteData, scopedSites]);

	useEffect(() => {
		setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, selectedSiteData));
	}, [scopedAllProvisionedMachines, selectedCustomer, selectedSiteData]);

	useEffect(() => {
		if (selectedCustomer) {
			const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(selectedCustomer?.cus_id));
			setSites(filtered);
			setEmptySites(filtered.length === 0);
			return;
		}
		setSites(scopedSites);
		setEmptySites(false);
	}, [scopedSites, selectedCustomer]);

	useEffect(() => {
		if (provisionCustomer) {
			setProvisionSites(scopedSites.filter((site) => Number(site?.cus_id) === Number(provisionCustomer?.cus_id)));
			return;
		}
		setProvisionSites(scopedSites);
	}, [scopedSites, provisionCustomer]);

	const filterSitesForCustomer = (customer) => {
		setSitesLoading(true);
		if (!customer) {
			setSites(scopedSites);
			setEmptySites(false);
			setSitesLoading(false);
			return;
		}

		const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(customer?.cus_id));
		setSites(filtered);
		setEmptySites(filtered.length === 0);
		setSitesLoading(false);
	};

	const handleEditSite = async (selectedSiteData) => {
		try {
			// Fetch all machines
			const respMachines = await getMachines();
			const allMachines = respMachines.data;

			setAssignedMachines(
				applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, selectedSiteData)
			);
			setNonAssignedMachines(allMachines);

			// Do something with filterMachines and nonProvisionedMachines as needed
		} catch (error) {
			console.error("Error fetching machines: ", error);
		}
	};

	const loadMachinesForProvision = async () => {
		try {
			const respMachines = await getMachines();
			setNonAssignedMachines(respMachines.data || []);
		} catch (error) {
			console.error("Error fetching machines: ", error);
		}
	};

	const updateProvisionSites = (customer) => {
		if (!customer) {
			setProvisionSites(scopedSites);
			return;
		}
		const filtered = scopedSites.filter((site) => Number(site?.cus_id) === Number(customer?.cus_id));
		setProvisionSites(filtered);
	};

	const handleProvisionFormSubmit = async () => {
		setSubmittingProvision(true);
		const requiredFields = [
			{ field: "machine_name", label: t('TABLE_HEADERS.MACHINE_NAME') },
			{ field: "machine_serial_number", label: t('LABELS.MACHINE_SERIAL_NUMBER') },
			{ field: "provision_date", label: t('LABELS.PROVISION_DATE') },
		];

		const nextErrors = {};
		requiredFields.forEach(({ field, label }) => {
			if (!`${provisionFormData[field] || ""}`.trim()) {
				nextErrors[field] = `${label} is required`;
			}
		});
		if (!provisionCustomer) {
			nextErrors.cus_id = t('LABELS.SELECT_A_CUSTOMER');
		}
		if (!provisionSite) {
			nextErrors.site_id = t('LABELS.SELECT_CUSTOMER_SITE');
		}

		const newSerial = normalizeSerial(provisionFormData.machine_serial_number);
		const duplicateComposite = applyProvisionFilters(
			scopedAllProvisionedMachines,
			provisionCustomer,
			provisionSite
		).some(
			(machine) =>
				machine.machine_number === provisionFormData.machine_number &&
				normalizeSerial(machine.sm_machine_product_name) === newSerial
		);
		if (duplicateComposite) {
			nextErrors.machine_serial_number = t('GENERAL.THIS_MACHINE_WITH_THE_SAME_MACHINE_SERIAL_ALREADY_EXISTS');
		}

		if (Object.keys(nextErrors).length > 0) {
			setValidationErrors(nextErrors);
			setErrorMessage(t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS'));
			setSubmittingProvision(false);
			return;
		}

		setValidationErrors({});
		setErrorMessage("");
		setProvisionServerError("");

		const req_body = [
			{
				MachineNumber: provisionFormData.machine_number,
				MachineName: provisionFormData.machine_name,
				MachineSerialNo: provisionFormData.machine_serial_number,
				CustomerNumber: provisionFormData.cus_number,
				SiteNumber: provisionFormData.site_number,
			},
		];

		try {
			const responseProvision = await provisionMachinesForCustomerSite(req_body);
			
			setSubmittingProvision(false);

			const results = responseProvision.data;
			const successResults = [];
			const errorResults = [];
			const existsResults = [];

			// Categorize results by status
			results.forEach((result) => {
				if (result.status === "inserted") {
					const data = {
						machine_id: result.data.machine_id,
						machine_name: result.data.machine_name,
						sm_machine_product_name: result.data.machine_product_name,
						machine_number: result.data.machine_number,
						global_product_code:
							result.data.global_product_code ||
							provisionFormData.global_product_code ||
							machineSelected?.global_product_code ||
							"",
						sm_machine_status: result.data.machine_status,
						sm_machine_id: result.data.id,
						customer_number: provisionCustomer?.customer_number || "",
						customer_name: provisionCustomer?.customer_name || "",
						customer_site_number: provisionSite?.customer_site_number || "",
						customer_site_name: provisionSite?.customer_site_name || "",
						site_id: provisionSite?.site_id,
						cus_id: provisionCustomer?.cus_id,
					};

					setAssignedMachines((prev) => {
						return [...prev, data]; // This will append the new 'data' object to the previous array
					});
					setAllProvisionedMachines((prev) => [...prev, data]);
					successResults.push(result);
				} else if (result.status === "error") {
					errorResults.push(result);
				} else if (result.status === "exists") {
					existsResults.push(result);
				}
			});

			if (existsResults.length > 0) {
				setValidationErrors((prev) => ({
					...prev,
					machine_name: t('GENERAL.MACHINE_ALREADY_PROVISIONED_FOR_THIS_SITE'),
				}));
				setProvisionServerError(
					`Machine ${provisionFormData.machine_number} ${t('GENERAL.IS_ALREADY_PROVISIONED_FOR_THIS_SITE_PLEASE_REMOVE_OR_EDIT_THE_EXISTING_PROVISION_BEFORE_ADDING_IT_AGAIN')}`
				);
				return;
			}

			// Construct the success message
			let successMessage = "";
			if (successResults.length > 0) {
				successMessage += `<b>${successResults.length}</b> ${t('GENERAL.MACHINES_SUCCESSFULLY_PROVISIONEDBR')}`;
				successResults.forEach((result, index) => {
					successMessage += `${index + 1}. Machine <b>${result.data.machine_name}</b> provisioned to <b>${provisionFormData.site_name
						}</b>.<br>`;
				});
			}

			// Construct the error message
			let errorMessage = "";
			if (errorResults.length > 0) {
				errorMessage += `<b>${errorResults.length}</b> ${t('GENERAL.MACHINES_FAILED_TO_PROVISION_DUE_TO_ERRORSBR')}`;
				errorResults.forEach((result, index) => {
					errorMessage += `${index + 1}. Machine <b>${result.data?.machine_name || "Unknown"}</b>: ${result.message
						}<br>`;
				});
			}

			// Construct the exists message
			let existsMessage = "";
			if (existsResults.length > 0) {
				existsMessage += `<b>${existsResults.length}</b> ${t('GENERAL.MACHINES_ALREADY_EXIST_IN_THE_SYSTEMBR')}`;
				existsResults.forEach((result, index) => {
					existsMessage += `${index + 1}. Machine <b>${result.data?.machine_name || "Unknown"}</b>: ${result.message
						}<br>`;
				});
			}

			// Display Swal
			Swal.fire({
				icon: successResults.length > 0 ? "success" : "info",
				title: t('TITLES.PROVISIONING_RESULTS'),
				html: `${successMessage}${errorMessage}${existsMessage}`,
			});

			// Reset the form only if provisioning was successful
			if (successResults.length > 0) {
				setProvisionFormData({
					machine_name: "",
					machine_number: "",
					global_product_code: "",
					machine_serial_number: "",
					provision_date: "",
					status: "active",
					site_name: "",
					cus_name: "",
					site_id: "",
					cus_id: "",
					machine_id: "",
					site_number: "",
					cus_number: "",
				});
				setNewProvision(false);
				setMachineSelected(null);
			}
		} catch (error) {
			console.error(error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.PLEASE_TRY_AGAIN'),
			});
		}

		// Proceed with your API call or logic
	};

	const handleDeleteProvisions = async () => {
		try {
			const confirmation = await Swal.fire({
				title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
				text: t('GENERAL.THIS_WILL_DELETE_THE_PROVISIONS_PERMANENTLY'),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText: t('GENERAL.YES_DELETE_IT'),
			});

			if (!confirmation.isConfirmed) return;

			const req_body = {
				sm_ids: selectedRows,
			};
			const response = await deleteProvisionsForCustomerSite(req_body);
			
			if (response.status) {
				setAssignedMachines((prevList) => prevList.filter((row) => !selectedRows.includes(row.sm_machine_id)));
				setAllProvisionedMachines((prevList) => prevList.filter((row) => !selectedRows.includes(row.sm_machine_id)));
				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.SUCCESS'),
					html: t('ALERT_MESSAGES.SUCCFULLY_MESSAGE'),
				});
			}
		} catch (error) {
			
		}
	};

	const handleRowSelect = (rows) => {
		setSelectedRows(rows);
	};

	const openEditModalFromRow = (row) => {
		setEditValidationErrors({});
		setEditError("");
		setEditFormData({
			id: row.sm_machine_id,
			machine_name: row.machine_name || "",
			machine_number: row.machine_number || "",
			machine_serial_number: row.sm_machine_product_name || "",
			status: row.sm_machine_status || t("MENU_ITEMS.ACTIVE"),
			cus_name: row.customer_name || selectedCustomer?.customer_name || "",
			site_name: row.customer_site_name || selectedSiteData?.customer_site_name || "",
		});
		setEditProvision(true);
	};

	const deleteSingleProvision = async (row) => {
		try {
			const confirmation = await Swal.fire({
				title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
				text: t('GENERAL.THIS_WILL_DELETE_THE_PROVISION_PERMANENTLY'),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#3085d6",
				confirmButtonText: t('GENERAL.YES_DELETE_IT'),
			});

			if (!confirmation.isConfirmed) return;

			const response = await deleteProvisionsForCustomerSite({ sm_ids: [row.sm_machine_id] });
			
			if (response.status) {
				setAssignedMachines((prevList) => prevList.filter((r) => r.sm_machine_id !== row.sm_machine_id));
				setAllProvisionedMachines((prevList) => prevList.filter((r) => r.sm_machine_id !== row.sm_machine_id));
				Swal.fire({ icon: "success", title: t('ALERT_MESSAGES.SUCCESS'), html: t('ALERT_MESSAGES.SUCCFULLY_MESSAGE') });
			}
		} catch (error) {
			
		}
	};

	const handleEditFieldChange = (e) => {
		const { name, value } = e.target;
		setEditFormData((p) => ({ ...p, [name]: value }));
		if (name === "machine_serial_number") {
			setEditValidationErrors((prev) => ({ ...prev, machine_serial_number: "" }));
			if (editError) setEditError("");
		}
	};

	const submitEditProvision = async () => {
		try {
			setEditSubmitting(true);
			const nextErrors = {};
			const normalizedSerial = normalizeSerial(editFormData.machine_serial_number);
			if (!normalizedSerial) {
				nextErrors.machine_serial_number = t('GENERAL.MACHINE_SERIAL_IS_REQUIRED');
			} else {
				const conflict = assignedMachines.some(
					(machine) =>
						machine.sm_machine_id !== editFormData.id &&
						machine.machine_number === editFormData.machine_number &&
						normalizeSerial(machine.sm_machine_product_name) === normalizedSerial
				);
				if (conflict) {
					nextErrors.machine_serial_number = t('GENERAL.THIS_MACHINE_WITH_THE_SAME_MACHINE_SERIAL_ALREADY_EXISTS');
				}
			}

			if (Object.keys(nextErrors).length > 0) {
				setEditValidationErrors(nextErrors);
				setEditError('Please fix the highlighted fields.');
				setEditSubmitting(false);
				return;
			}

			setEditValidationErrors({});
			setEditError("");

			const payload = {
				machine_status: editFormData.status,
				machine_product_name: editFormData.machine_serial_number,
				// machine_name can be enabled later if needed
			};
			const resp = await updateProvisionForCustomerSite(editFormData.id, payload);
			
			if (resp.status) {
				// Update local grid row
				setAssignedMachines((prev) => prev.map((r) => {
					if (r.sm_machine_id === editFormData.id) {
						return {
							...r,
							sm_machine_status: editFormData.status,
							sm_machine_product_name: editFormData.machine_serial_number,
							machine_name: editFormData.machine_name,
						};
					}
					return r;
				}));
				setAllProvisionedMachines((prev) => prev.map((r) => {
					if (r.sm_machine_id === editFormData.id) {
						return {
							...r,
							sm_machine_status: editFormData.status,
							sm_machine_product_name: editFormData.machine_serial_number,
							machine_name: editFormData.machine_name,
						};
					}
					return r;
				}));
				Swal.fire({ icon: "success", title: t('ALERT_MESSAGES.SUCCESS'), text: t('GENERAL.PROVISION_UPDATED_SUCCESSFULLY') });
				setEditProvision(false);
			} else {
				Swal.fire({ icon: "error", title: t('ALERT_MESSAGES.ERROR'), text: resp?.data?.message || t('GENERAL.FAILED_TO_UPDATE_PROVISION') });
			}
		} catch (err) {
			console.error(err);
			Swal.fire({ icon: "error", title: t('ALERT_MESSAGES.ERROR'), text: t('GENERAL.SOMETHING_WENT_WRONG_WHILE_UPDATING_THE_PROVISION') });
		} finally {
			setEditSubmitting(false);
		}
	};

	const customButtons = [
		// Super-admin-only bulk STEP refresh, on the Manage Machine Provisions page only.
		...((isSuperAdmin && hideAdminFilters) ? [{
			label: bulkStepSyncing
				? "Refreshing STEP Files..."
				: selectedRows.length > 0
					? `Refresh STEP Files (${selectedRows.length})`
					: "Refresh All STEP Files",
			icon: bulkStepSyncing ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <RefreshIcon />,
			onClick: () => {
				handleBulkStepSync();
			},
			disabled: bulkStepSyncing,
		}] : []),
		...((isSuperAdmin || hasPermission('machine_provision_management.provision_machines')) ? [{
			label: t('LABELS.ADD_NEW_PROVISION'),
			icon: <AddCircle />,
			onClick: () => {
				resetProvisionForm();
				setProvisionCustomer(selectedCustomer || null);
				setProvisionSite(selectedSiteData || null);
				updateProvisionSites(selectedCustomer || null);
				loadMachinesForProvision();
				if (selectedCustomer) {
					setProvisionFormData((prev) => ({
						...prev,
						cus_id: selectedCustomer.cus_id,
						cus_number: selectedCustomer.customer_number,
						cus_name: selectedCustomer.customer_name,
					}));
				}
				if (selectedSiteData) {
					setProvisionFormData((prev) => ({
						...prev,
						site_id: selectedSiteData.site_id,
						site_number: selectedSiteData.customer_site_number,
						site_name: selectedSiteData.customer_site_name,
					}));
				}
				setNewProvision(true);
			},
		}] : []),
		...((isSuperAdmin || hasPermission('machine_provision_management.delete_provisions')) ? [{
			label: t('LABELS.DELETE_SELECTED'),
			color: "error",
			icon: <Delete />,
			onClick: () => {
				handleDeleteProvisions();
			},
			disabled: selectedRows.length === 0,
		}] : [])
	]

	const columns = [
		{
			field: "customer_number",
			headerName: t('TABLE_HEADERS.CUSTOMER_NUMBER'),
			width: 160,
			hideable: false,      // Makes customer_number column constant (non-hideable)
			filterable: false,    // Removes filter option
		},
		{
			field: "customer_site_number",
			headerName: t('TABLE_HEADERS.SITE_NUMBER'),
			width: 160,
			filterable: false,    // Removes filter option
		},
		{
			field: "customer_site_name",
			headerName: t('LABELS.SITE_NAME'),
			flex: 1,
			filterable: false,    // Removes filter option
		},
		{
			field: "machine_name",
			headerName: t('TABLE_HEADERS.MACHINE_NAME'),
			flex: 1,
			filterable: false,    // Removes filter option
		},
		{
			field: "global_product_code",
			headerName: t('TABLE_HEADERS.GLOBAL_PRODUCT_CODE'),
			width: 220,
			align: "center",
			headerAlign: "center",
			filterable: false,
		},
		{
			field: "sm_machine_product_name",
			headerName: t('LABELS.MACHINE_SERIAL'),
			width: 200,
			align: "center",
			headerAlign: "center",
			hide: true,           // Hides Machine Serial column by default
			filterable: false,    // Removes filter option
			renderCell: (params) => {
				const statusText =
					params.row.sm_machine_product_name === "EMPTY"
						? `<span style="font-size: 10px; color: #aaa">NA</span>`
						: params.row.sm_machine_product_name;
				return <span dangerouslySetInnerHTML={{ __html: statusText }} />;
			},
		},
		// {
		// 	field: "machine_number",
		// 	headerName: t('TABLE_HEADERS.MACHINE_NUMBER'),
		// 	width: 200,
		// 	align: "center",
		// 	headerAlign: "center",
		// 	filterable: false,    // Removes filter option
		// },
		{
			field: "sm_machine_status",
			headerName: t('TABLE_HEADERS.MACHINE_STATUS'),
			width: 200,
			align: "center",
			headerAlign: "center",
			filterable: false,    // Removes filter option
			renderCell: (params) => {
				const status = params.row.sm_machine_status;

				// Handle "TRUE"/"FALSE" strings from API, plus other variations
				const isActive =
					status === 'TRUE' ||      // API returns this
					status === 'True' ||
					status === 'true' ||
					status === 'Active' ||
					status === 'active' ||
					status === true ||
					status === 1;

				return (
					<Chip
						label={isActive ? 'Active' : 'Inactive'}
						color={isActive ? 'success' : 'error'}
						size="small"
						variant="filled"
					/>
				);
			},
		},
		...(showDocumentFetchColumn
			? [{
				field: "document_fetch",
				headerName: "Refresh Document",
				width: 170,
				align: "center",
				headerAlign: "center",
				sortable: false,
				filterable: false,
				renderCell: (params) => {
					const machineNumber = `${params?.row?.machine_number || ""}`.trim();
					const isSyncing = syncingMachineNumber === machineNumber;
					return (
						<Tooltip title="Refresh Documents" arrow>
							<span>
								<IconButton
									size="small"
									disabled={!machineNumber || isSyncing}
									onClick={() => handleTriggerDocumentFetch(params.row)}
									aria-label="Refresh Documents"
									sx={{ color: "error.main" }}
								>
									{isSyncing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
								</IconButton>
							</span>
						</Tooltip>
					);
				},
			}]
			: []),
		...(showDocumentFetchColumn
			? [{
				field: "model_3d_sync",
				headerName: "3D Model Sync",
				width: 250,
				align: "center",
				headerAlign: "center",
				sortable: false,
				// The only filterable column on this grid: its "Filter" menu item opens the
				// panel with a dropdown of every state the chip can show.
				filterable: true,
				type: "singleSelect",
				valueOptions: MODEL_STATE_FILTER_OPTIONS,
				valueGetter: modelStateLabelForRow,
				renderCell: (params) => {
					const machineNumber = `${params?.row?.machine_number || ""}`.trim();
					const siteId = Number(params?.row?.site_id);
					const isReloading = reloadingMachineNumber === machineNumber;
					const isSiteSyncing = siteStepSyncLoadingId === siteId;
					const modelState =
						MODEL_STATE_CHIP[params?.row?.model_state] || MODEL_STATE_CHIP.not_requested;
					// A sync is already running for this machine, so the button would
					// only ever report "already queued" — hide it and let the chip speak.
					const syncInFlight = ["in_progress", "available_updating"].includes(
						String(params?.row?.model_state || "")
					);
					return (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, height: "100%" }}>
							<Tooltip title={modelState.tooltip} arrow>
								<Chip
									size="small"
									label={modelState.label}
									color={modelState.color}
									variant={modelState.color === "default" ? "outlined" : "filled"}
									sx={{ minWidth: 96 }}
								/>
							</Tooltip>
							{(!syncInFlight || isReloading) && (
								<Tooltip title="Sync Machine 3D Model" arrow>
									<span>
										<IconButton
											size="small"
											disabled={!machineNumber || isReloading}
											onClick={() => handleTriggerReload3DModel(params.row)}
											aria-label="Sync Machine 3D Model"
											sx={{ color: "primary.main" }}
										>
											{isReloading ? <CircularProgress size={16} /> : <ViewInArIcon fontSize="small" />}
										</IconButton>
									</span>
								</Tooltip>
							)}
							{(!syncInFlight || isSiteSyncing) && (
								<Tooltip title="Sync Bakery Location 3D Models" arrow>
									<span>
										<IconButton
											size="small"
											disabled={!siteId || isSiteSyncing}
											onClick={() => handleTriggerSiteStepSync(params.row)}
											aria-label="Sync Bakery Location 3D Models"
											sx={{ color: "primary.main" }}
										>
											{isSiteSyncing ? <CircularProgress size={16} /> : <WarehouseIcon fontSize="small" />}
										</IconButton>
									</span>
								</Tooltip>
							)}
						</Box>
					);
				},
			}]
			: []),
		...(hideAdminFilters ? [{
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 150,
			headerAlign: 'center',
			align: 'center',
			filterable: false,    // Removes filter option
			renderCell: (params) => {
				const canEdit = isSuperAdmin || hasPermission('machine_provision_management.edit_provisions');
				const canDelete = isSuperAdmin || hasPermission('machine_provision_management.delete_provisions');
				const machineNumber = `${params?.row?.machine_number || ""}`.trim();
				const isReloading = reloadingMachineNumber === machineNumber;
				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
						<Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
							{isSuperAdmin && (
								<Tooltip title="Reload 3D Model" arrow>
									<span>
										<IconButton
											disabled={!machineNumber || isReloading}
											onClick={() => handleTriggerReload3DModel(params.row)}
											sx={{ color: "error.main" }}
										>
											{isReloading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
										</IconButton>
									</span>
								</Tooltip>
							)}
							<IconButton color="primary" disabled={!canEdit} onClick={() => openEditModalFromRow(params.row)}>
								<EditIcon />
							</IconButton>
							<IconButton color="error" disabled={!canDelete} onClick={() => deleteSingleProvision(params.row)}>
								<Delete />
							</IconButton>
						</Box>
					</Box>
				)
			},
		}] : []),
	];


	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
				<DataGridSkeletonForEquipment />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	let label = t('LABELS.CUSTOMER_VIEW_LOCATIONS');

	// Check if siteLoading is true

	if (emptySites) {
		label = t('LABELS.NO_SITES_FOUND_CUSTOMER');
	} else if (!sitesLoading && sites.length === 0) {
		label = t('LABELS.CUSTOMER_VIEW_LOCATIONS');
	} else if (sitesLoading) {
		label = t('LABELS.LOADING');
	} else if (!sitesLoading && sites.length > 0) {
		label = t('LABELS.SELECT_CUSTOMER_SITE');
	}

	return (
		<Box sx={{
			width: '100%',
			maxWidth: '100%',  // Don't exceed parent width
			overflow: 'hidden',
			boxSizing: 'border-box'
		}}>
			{!hideFilters && (
				<Box
					sx={{
						mb: 2,
						mt: 1,
						display: "flex",
						flexDirection: "column",
						rowGap: '5px',
						background: '#eee',
						p: 2,
						borderRadius: '10px',
						maxWidth: '100%',
						boxSizing: 'border-box'
					}}>
					{!hideAdminFilters && (<>
						<Typography fontWeight={600} mb={0.5}>  {t('LABELS.ADMIN_FILTERS_TITLE')}</Typography>
						<Typography variant="body2" color="text.secondary" mb={2}>
							{t('LABELS.ADMIN_FILTERS_DESCRIPTION')}
						</Typography>
					</>)}
					<Typography variant="body1" fontWeight={"bold"}>Filters: </Typography>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							columnGap: "20px",
							width: '100%',
							maxWidth: '100%'
						}}>

						<Autocomplete
							options={scopedCustomers}
							getOptionLabel={(option) => option.customer_name}
							value={selectedCustomer}
							isOptionEqualToValue={(option, value) => Number(option?.cus_id) === Number(value?.cus_id)}
							onChange={(event, newValue) => {
								setSelectedCustomer(newValue);
								setSelectedSiteData(null);
								setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, newValue, null));
								setNonAssignedMachines([]);
								resetProvisionForm();
								if (newValue) {
									filterSitesForCustomer(newValue);
								} else {
									filterSitesForCustomer(null);
									setEmptySites(false);
								}
							}}
							filterOptions={customerSiteFilterOptions}
							renderInput={(params) => <TextField {...params} label={t('LABELS.SELECT_A_CUSTOMER')} variant="outlined" />}
							fullWidth
							size="small"
						/>

						<Autocomplete
							options={sites}
							getOptionLabel={(option) =>
								`${option.customer_site_name} - ${option.customer_site_number}` || t('UI_TEXT.NO_SITES_AVAILABLE')
							}
							value={selectedSiteData}
							isOptionEqualToValue={(option, value) => Number(option?.site_id) === Number(value?.site_id)}
							onChange={(event, newValue) => {
								setSelectedSiteData(newValue);
								if (newValue) {
									const parentCustomer = scopedCustomers.find((customer) => Number(customer?.cus_id) === Number(newValue?.cus_id));
									if (parentCustomer) {
										setSelectedCustomer(parentCustomer);
										filterSitesForCustomer(parentCustomer);
									}
									setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, parentCustomer || selectedCustomer, newValue));
									handleEditSite(newValue);
								} else {
									setAssignedMachines(applyProvisionFilters(scopedAllProvisionedMachines, selectedCustomer, null));
									setNonAssignedMachines([]);
									setSelectedSiteData(null);
								}
							}}
							filterOptions={customerSiteFilterOptions}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									placeholder={sitesLoading ? t('LABELS.LOADING') : t('LABELS.CUSTOMER_VIEW_LOCATIONS')}
								/>
							)}
							fullWidth
							size="small"
							disabled={sitesLoading || sites.length === 0}
						/>
					</Box>

				</Box>
			)}

			<Box sx={{ px: 1 }}>
				<Typography variant="h6">
					{selectedSiteData
						? `${t('UI_TEXT.PROVISIONED_MACHINES')} ${selectedSiteData.customer_site_name}`
						: selectedCustomer
							? `${t('UI_TEXT.PROVISIONED_MACHINES')} ${selectedCustomer.customer_name}`
							: `${t('MENU_ITEMS.ALL')} ${t('UI_TEXT.PROVISIONED_MACHINES')}`}
				</Typography>

				{/* <Datagrid
					data={assignedMachines}
					columns={columns}
					customButtons={customButtons}
					checkboxSelection={true}
					rowIdField="sm_machine_id"
					onSelect={handleRowSelect}
					sortBy={[{ field: "machine_name", sort: "asc" }]}
					hideFilterFromColumnMenu
					lockActiveColumnInManageColumns
					preventHidingLastColumn
				/> */}
				{/*  */}
				<Box sx={{ px: 1, maxWidth: '100%' }}>
					<Box sx={{
						width: '100%',
						overflow: 'auto',        // Enable horizontal scroll
						px: 1,
						'&::-webkit-scrollbar': {
							height: '8px'
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: '#888',
							borderRadius: '4px'
						}
					}}>
							<Box sx={{ minWidth: '1200px' }}>
								<Datagrid
									data={assignedMachines}
									columns={columns}
									customButtons={customButtons}
									rowIdField="sm_machine_id"
									onSelect={handleRowSelect}
									// Provisions page only — the Admin Documentation > Equipment view keeps
									// its read-only grid. pageScopedHeaderSelection={false} hands select-all
									// back to MUI so the header checkbox picks every filtered row, not just
									// the current page.
									checkboxSelection={hideAdminFilters}
									pageScopedHeaderSelection={false}
									sortBy={[{ field: "machine_name", sort: "asc" }]}
									autoHeight={true}
									sx={{
										width: '100%'
									}}
								/>
							</Box>
					</Box>
				</Box>
				{!hideFilters && (<>
					<ModalWrapper open={newProvision}
						setOpen={setNewProvision}
						onClose={() => {
							resetProvisionForm();
							setNewProvision(false);
						}}
						title={t('UI_TEXT.ADD_NEW_PROVISION')}
						content={
							<>
								{provisionServerError && (
									<Alert severity="error" sx={{ width: "100%" }}>
										{provisionServerError}
									</Alert>
								)}
								<Box
									py={2}
									px={1}
									display="flex"
									flexDirection="column"
									gap={2}
									justifyContent="flex-start"
									alignItems="flex-start"
									style={{ width: "100%", minHeight: "350px" }}>
									<Autocomplete
										sx={{ width: "100%" }}
										options={scopedCustomers}
										getOptionLabel={(option) => option.customer_name}
										value={provisionCustomer}
										isOptionEqualToValue={(option, value) => Number(option?.cus_id) === Number(value?.cus_id)}
										onChange={(event, newValue) => {
											setProvisionCustomer(newValue);
											setProvisionSite(null);
											updateProvisionSites(newValue);
											setProvisionFormData((prev) => ({
												...prev,
												cus_id: newValue?.cus_id || "",
												cus_number: newValue?.customer_number || "",
												cus_name: newValue?.customer_name || "",
												site_id: "",
												site_number: "",
												site_name: "",
											}));
											setValidationErrors((prev) => ({ ...prev, cus_id: "", site_id: "" }));
										}}
										filterOptions={customerSiteFilterOptions}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t('LABELS.SELECT_A_CUSTOMER')}
												variant="outlined"
												size="small"
												error={Boolean(validationErrors.cus_id)}
												helperText={validationErrors.cus_id || ""}
											/>
										)}
										{...modalAutocompleteProps}
									/>
									<Autocomplete
										sx={{ width: "100%" }}
										options={provisionSites}
										getOptionLabel={(option) =>
											`${option.customer_site_name} - ${option.customer_site_number}` || t('UI_TEXT.NO_SITES_AVAILABLE')
										}
										value={provisionSite}
										isOptionEqualToValue={(option, value) => Number(option?.site_id) === Number(value?.site_id)}
										onChange={(event, newValue) => {
											setProvisionSite(newValue);
											if (newValue && !provisionCustomer) {
												const parentCustomer = scopedCustomers.find((customer) => Number(customer?.cus_id) === Number(newValue?.cus_id)) || null;
												setProvisionCustomer(parentCustomer);
												updateProvisionSites(parentCustomer);
												setProvisionFormData((prev) => ({
													...prev,
													cus_id: parentCustomer?.cus_id || "",
													cus_number: parentCustomer?.customer_number || "",
													cus_name: parentCustomer?.customer_name || "",
												}));
											}
											setProvisionFormData((prev) => ({
												...prev,
												site_id: newValue?.site_id || "",
												site_number: newValue?.customer_site_number || "",
												site_name: newValue?.customer_site_name || "",
											}));
											setValidationErrors((prev) => ({ ...prev, site_id: "" }));
										}}
										filterOptions={customerSiteFilterOptions}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t('LABELS.SELECT_CUSTOMER_SITE')}
												variant="outlined"
												size="small"
												error={Boolean(validationErrors.site_id)}
												helperText={validationErrors.site_id || ""}
											/>
										)}
										fullWidth
										size="small"
										disabled={provisionSites.length === 0}
										{...modalAutocompleteProps}
									/>
									<Autocomplete
										sx={{ width: "100%" }}
										options={nonAssignedMachines}
										getOptionLabel={(option) => `${option.machine_number} - ${option.machine_name}`}
										renderInput={(params) => (
											<TextField {...params} label={t('LABELS.SELECT_MACHINE')} variant="outlined" size="small" />
										)}
										onChange={(event, value) => {
											if (value) {
												setProvisionServerError("");
												setProvisionFormData((prevData) => ({
													...prevData,
													machine_id: value.machine_id || "",
													machine_name: value.machine_name || "",
													machine_number: value.machine_number || "",
													global_product_code: value.global_product_code || "",
													provision_date: new Date().toISOString().split("T")[0],
												}));
												setValidationErrors({});
												setErrorMessage("");
												setMachineSelected(value);
											} else {
												setProvisionFormData((prevData) => ({
													...prevData,
													machine_id: "",
													machine_name: "",
													machine_number: "",
													global_product_code: "",
													machine_serial_number: "",
													provision_date: "",
												}));
												setMachineSelected(null);
											} // Assign the selected machine object
										}}
										isOptionEqualToValue={(option, value) =>
											option.machine_number === value?.machine_number
										}
										{...modalAutocompleteProps}
									/>

									{machineSelected && (
										<>
											<Typography variant="body1" fontWeight="bold">
												{t('UI_TEXT.MACHINE_DETAILS')}:{" "}
											</Typography>
											<FormGroup row sx={{ width: "100%", columnGap: "10px" }}>
												<TextField
													label={t('TABLE_HEADERS.MACHINE_NAME')}
													size="small"
													fullWidth
													variant="outlined"
													name="machine_name"
													sx={{ flex: 1 }}
													value={provisionFormData.machine_name}
													onChange={handleProvisionFormChange}
													disabled={!machineSelected}
													error={!!validationErrors.machine_name}
													helperText={validationErrors.machine_name || ""}
												/>

												{/* Machine Number */}
												<TextField
													label={t('TABLE_HEADERS.MACHINE_NUMBER')}
													size="small"
													fullWidth
													variant="outlined"
													name="machine_number"
													sx={{ flex: 1 }}
													value={provisionFormData.machine_number}
													onChange={handleProvisionFormChange}
													disabled
												/>

												{/* Machine Serial Number */}
												<TextField
													label={t('LABELS.MACHINE_SERIAL_NUMBER')}
													size="small"
													fullWidth
													variant="outlined"
													name="machine_serial_number"
													sx={{ flex: 1 }}
													value={provisionFormData.machine_serial_number}
													onChange={handleProvisionFormChange}
													disabled={!machineSelected}
													error={!!validationErrors.machine_serial_number}
													helperText={validationErrors.machine_serial_number || ""}
												/>
											</FormGroup>

											<Typography variant="body1" fontWeight="bold">
												{t('LABELS.PROVISION_DATE')}:{" "}
											</Typography>
											<TextField
												label={t('LABELS.PROVISION_DATE')}
												type="date"
												size="small"
												fullWidth
												InputLabelProps={{ shrink: true }}
												variant="outlined"
												name="provision_date"
												value={provisionFormData.provision_date}
												onChange={handleProvisionFormChange}
												disabled={!machineSelected}
												error={!!validationErrors.provision_date}
												helperText={validationErrors.provision_date || ""}
											/>
											<Typography variant="body1" fontWeight="bold">
												{UI_TEXT.PROVISION_FOR}:{" "}
											</Typography>
											<FormGroup row sx={{ width: "100%", columnGap: "10px" }}>
												<TextField
													label={t('TABLE_HEADERS.CUSTOMER_NAME')}
													size="small"
													fullWidth
													variant="outlined"
													name="cus_name"
													sx={{ flex: 1 }}
													disabled
													value={provisionFormData.cus_name}
													onChange={handleProvisionFormChange}
												/>

												<TextField
													label={t('LABELS.SITE_NAME')}
													size="small"
													fullWidth
													variant="outlined"
													name="site_name"
													sx={{ flex: 1 }}
													disabled
													value={provisionFormData.site_name}
													onChange={handleProvisionFormChange}
												/>
											</FormGroup>

											<Typography variant="body1" fontWeight="bold">
												{t('UI_TEXT.PROVISION_STATUS')}{" "}
											</Typography>
											<FormControl size="small" fullWidth disabled={!machineSelected}>
												<InputLabel>{t('TABLE_HEADERS.STATUS')}</InputLabel>
												<Select
													label={t('TABLE_HEADERS.STATUS')}
													name="status"
													value={provisionFormData.status}
													onChange={handleProvisionFormChange}
													MenuProps={{
														disablePortal: false,
														slotProps: { root: { className: "modal-menu-popover" } },
														PaperProps: { sx: { maxHeight: 300, overflowY: "auto" } },
														anchorOrigin: { vertical: "bottom", horizontal: "left" },
														transformOrigin: { vertical: "top", horizontal: "left" },
													}}>
													<MenuItem value="active">{t('MENU_ITEMS.ACTIVE')}</MenuItem>
													<MenuItem value="inactive">{t('MENU_ITEMS.INACTIVE')}</MenuItem>
												</Select>
											</FormControl>
										</>
									)}
								</Box>
								{errorMessage && (
									<Typography color="error" textAlign="center" marginBottom={2}>
										{errorMessage}
									</Typography>
								)}

							</>
						} actions={<>
							<Button
								variant="outlined"
								onClick={() => {
									resetProvisionForm();
									setNewProvision(false);
								}}>
								{t('BUTTON.CANCEL')}
							</Button>
							<Button
								variant="contained"
								onClick={handleProvisionFormSubmit}
								disabled={!machineSelected}>
								{submittingProvision ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />{" "}
										{t('BUTTON.SAVING')}
									</>
								) : (
									t('BUTTON.SUBMIT_PROVISION')
								)}
							</Button>
						</>} />


					<ModalWrapper open={editProvision}
						setOpen={setEditProvision}
						onClose={() => setEditProvision(false)}
						title={"Edit Provision"}
						content={<>
							<Box display="flex" flexDirection="column" gap={2} px={1} py={2} justifyContent="flex-start" alignItems="flex-start" style={{ width: "100%", minHeight: "250px" }}>
								<TextField
									label={t('TABLE_HEADERS.MACHINE_NAME')}
									size="small"
									fullWidth
									variant="outlined"
									name="machine_name"
									sx={{ flex: 1 }}
									value={editFormData.machine_name}
									onChange={handleEditFieldChange}
									disabled
								/>
								<TextField
									label={t('TABLE_HEADERS.MACHINE_NUMBER')}
									size="small"
									fullWidth
									variant="outlined"
									name="machine_number"
									sx={{ flex: 1 }}
									value={editFormData.machine_number}
									onChange={handleEditFieldChange}
									disabled
								/>


								<TextField
									label={t('LABELS.MACHINE_SERIAL_NUMBER')}
									size="small"
									fullWidth
									variant="outlined"
									name="machine_serial_number"
									sx={{ flex: 1 }}
									value={editFormData.machine_serial_number}
									onChange={handleEditFieldChange}
									error={Boolean(editValidationErrors.machine_serial_number)}
									helperText={editValidationErrors.machine_serial_number || ""}
								/>
								<Typography variant="body1" fontWeight="bold" >
									{t('UI_TEXT.PROVISION_STATUS')} {" "}
								</Typography>
								<FormControl size="small" fullWidth>
									<InputLabel>{t('TABLE_HEADERS.STATUS')}</InputLabel>
									<Select
										label={t('TABLE_HEADERS.STATUS')}
										name="status"
										value={editFormData.status}
										onChange={handleEditFieldChange}
										MenuProps={{
											disablePortal: false,
											slotProps: { root: { className: "modal-menu-popover" } },
											PaperProps: { sx: { maxHeight: 300, overflowY: "auto" } },
											anchorOrigin: { vertical: "bottom", horizontal: "left" },
											transformOrigin: { vertical: "top", horizontal: "left" },
										}}>
										<MenuItem value={t('MENU_ITEMS.ACTIVE')}>{t('MENU_ITEMS.ACTIVE')}</MenuItem>
										<MenuItem value={t('MENU_ITEMS.INACTIVE')}>{t('MENU_ITEMS.INACTIVE')}</MenuItem>
									</Select>
								</FormControl>

								<Typography variant="body1" fontWeight="bold">
									{t('UI_TEXT.PROVISION_FOR')}{" "}
								</Typography>
								<TextField
									label={t('TABLE_HEADERS.CUSTOMER_NAME')}
									size="small"
									fullWidth
									variant="outlined"
									name="cus_name"
									sx={{ flex: 1 }}
									disabled
									value={editFormData.cus_name || selectedCustomer?.customer_name || ""}
								/>

								<TextField
									label={t('LABELS.SITE_NAME')}
									size="small"
									fullWidth
									variant="outlined"
									name="site_name"
									sx={{ flex: 1 }}
									disabled
									value={editFormData.site_name || selectedSiteData?.customer_site_name || ""}
								/>

								{editError && (
									<Typography color="error" variant="body2">
										{editError}
									</Typography>
								)}
							</Box>
						</>}
						actions={<>
							<Button variant="outlined" onClick={() => setEditProvision(false)}>
								{t('BUTTON.CANCEL')}
							</Button>
							<Button variant="contained" onClick={submitEditProvision} disabled={editSubmitting}>
								{editSubmitting ? (
									<>
										<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} /> {t('BUTTON.SAVING')}
									</>
								) : (
									t("BUTTON.SAVE_CHANGES")
								)}
							</Button>
						</>}

					/>
				</>
				)}
			</Box>
		</Box >
	);
};

export default ManageSiteMachines;
